import { useState, useMemo } from 'react';
import { Case } from '../types';
import { Filter } from '../components/cases/Filters/types';

export const useCaseFilters = (cases: Case[]) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [practiceAreaFilter, setPracticeAreaFilter] = useState<string>('all');
  const [advancedFilters, setAdvancedFilters] = useState<Filter[]>([]);

  // Get unique practice areas for filter options
  const practiceAreas = useMemo(() => {
    const areas = new Set(cases.map(c => c.practiceArea));
    return Array.from(areas);
  }, [cases]);

  // Filter and search cases
  const filteredCases = useMemo(() => {
    return cases.filter(case_ => {
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = 
        case_.title.toLowerCase().includes(searchLower) ||
        case_.caseNumber.toLowerCase().includes(searchLower) ||
        case_.description.toLowerCase().includes(searchLower);

      const matchesStatus = statusFilter === 'all' || case_.status === statusFilter;
      const matchesPracticeArea = practiceAreaFilter === 'all' || case_.practiceArea === practiceAreaFilter;

      // Apply advanced filters
      const matchesAdvancedFilters = advancedFilters.every(filter => {
        switch (filter.type) {
          case 'stage':
            return case_.currentStageId === filter.value;
          case 'staff':
            return case_.assignedLawyerId === filter.value;
          case 'client':
            return case_.clientId === filter.value;
          case 'date':
            const filterDate = new Date(filter.value).toDateString();
            return case_.keyDates.some(date => 
              new Date(date.date).toDateString() === filterDate
            );
          default:
            return true;
        }
      });

      return matchesSearch && matchesStatus && matchesPracticeArea && matchesAdvancedFilters;
    });
  }, [cases, searchQuery, statusFilter, practiceAreaFilter, advancedFilters]);

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setPracticeAreaFilter('all');
    setAdvancedFilters([]);
  };

  const handleAdvancedFilterAdd = (filter: Filter) => {
    setAdvancedFilters(prev => [...prev, filter]);
  };

  const handleAdvancedFilterRemove = (filterId: string) => {
    setAdvancedFilters(prev => prev.filter(f => f.id !== filterId));
  };

  return {
    // Filter state
    searchQuery,
    statusFilter,
    practiceAreaFilter,
    advancedFilters,
    practiceAreas,
    filteredCases,

    // Actions
    setSearchQuery,
    setStatusFilter,
    setPracticeAreaFilter,
    handleAdvancedFilterAdd,
    handleAdvancedFilterRemove,
    clearFilters,
  };
};